import { useCallback, useEffect, useRef, useState } from 'react'
import { vn } from 'vend-number'
import { isGiftCard } from '../../../../functions/lineItem'
import { LineItem } from '../../../../models/line-item'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectLoggedInUser,
  selectLoggedInUserId,
} from '../../../../state/slices/posState/posStateSlice'
import {
  selectExpandedLineItemId,
  selectLineItemDiscountError,
  selectLineItemPriceInputError,
  selectLineItemQuantityInputError,
  setLineItemDiscountInputError,
  setLineItemPriceInputError,
  setLineItemQuantityInputError,
} from '../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'

export interface LineItemDisplayDetailsProps {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  elementToFocus?: 'quantity' | 'price'
}

/** This is based on $vd-time-s in css variable from helios css, we want to wait for the transition to finish before we focus on the input element otherwise it won't work */
const TRANSITION_DELAY = 200 // ms

export const useLineItemDisplayDetailsModel = ({
  lineItem,
  editAllowedForConfirmed,
  elementToFocus = 'quantity',
}: LineItemDisplayDetailsProps) => {
  const dispatch = useAppDispatch()
  const quantityError = useAppSelector(
    selectLineItemQuantityInputError(lineItem.localId)
  )
  const priceError = useAppSelector(
    selectLineItemPriceInputError(lineItem.localId)
  )
  const discountError = useAppSelector(
    selectLineItemDiscountError(lineItem.localId)
  )
  const showSalePerson = getFeatureFromCache('sales_person_attribution')
  const loggedInUser = useAppSelector(selectLoggedInUser)
  const loggedInUserID = useAppSelector(selectLoggedInUserId)
  const [hasUserSwitched, setHasUserSwitched] = useState<boolean>(false)

  const showSplitQuantityButton = !lineItem.giftCardNumber

  useEffect(() => {
    // Set to true for one render cycle
    setHasUserSwitched(true)
  }, [loggedInUserID])

  useEffect(() => {
    if (hasUserSwitched) {
      setHasUserSwitched(false)
    }
  }, [hasUserSwitched])

  const expandedLineItem = useAppSelector(selectExpandedLineItemId)
  const quantityInputRef = useRef<HTMLInputElement>(null)
  const priceInputRef = useRef<HTMLInputElement>(null)
  const onQuantityError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemQuantityInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const onPriceError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemPriceInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const onDiscountError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemDiscountInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const isPriceMarkedUp = vn(lineItem.displayMarkupPercent).gt(0)

  useEffect(() => {
    if (expandedLineItem !== lineItem.localId) {
      return
    }

    if (elementToFocus === 'price') {
      setTimeout(() => {
        priceInputRef.current?.focus()
        priceInputRef.current?.setSelectionRange(
          0,
          priceInputRef.current.value.length
        )
      }, TRANSITION_DELAY)

      return
    }

    setTimeout(() => {
      quantityInputRef.current?.focus()
      quantityInputRef.current?.setSelectionRange(
        0,
        quantityInputRef.current.value.length
      )
    }, TRANSITION_DELAY)
  }, [expandedLineItem, lineItem.localId, elementToFocus])

  const readonly = isGiftCard(lineItem.product)

  const productId = lineItem.product.variant_parent_id || lineItem.product.id

  return {
    quantityError,
    quantityInputRef,
    onQuantityError,

    priceError,
    priceInputRef,
    isPriceMarkedUp,
    onPriceError,

    discountError,
    onDiscountError,

    showSplitQuantityButton,
    hasUserSwitched,
    readonly,
    showSalePerson,
    loggedInUser,
    productId,
    editAllowedForConfirmed,
    lineItem,
  }
}
